import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayTime, ukDayYear } from "@/lib/time";
import { deskState, gbp, ladderFor, nowOf } from "@/lib/stages";
import { decisionDue } from "@/lib/caseflow";
import {
  Btn, CellMain, CellSub, DateCell, DeskSupport, Divider, Empty, Kv, Ladder, LinkRow, Money, Note, PageBody, PageHead, Panel, RailCard, Sec, StatusChip, Tabs, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { StagePicker } from "@/components/portal/sys/StagePicker";
import { NowPicker } from "@/components/portal/sys/NowPicker";
import { CaseThread } from "@/components/portal/crm/CaseThread";
import { SlaCountdown } from "@/components/portal/crm/SlaCountdown";
import { StageNudge } from "@/components/portal/crm/StageNudge";
import { LiveLine } from "@/components/portal/crm/LiveLine";
import { UpdateHistory } from "@/components/portal/crm/UpdateHistory";
import { CallCheck } from "@/components/portal/crm/CallCheck";
import { DocRequests } from "@/components/portal/DocRequests";
import { AskTheClient } from "@/components/portal/AskTheClient";
import { prettySize } from "@/components/portal/DocUploader";
import { I } from "@/components/portal/sys/icons";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";
import { CompanyCheckCard } from "@/components/portal/crm/company/CompanyCheckCard";
import { CompanyCheckReport } from "@/components/portal/crm/company/CompanyCheckReport";
import { useCompanyCheck } from "@/lib/companyCheck";
import { useLenderPitch } from "@/lib/dealPitch";
import { DealPitchCard, DealPitchView } from "@/components/portal/crm/pitch/DealPitch";
import { SendDialog } from "@/components/portal/crm/pitch/SendDialog";
import { SentLog } from "@/components/portal/crm/pitch/SentLog";
import { toast } from "sonner";

export default function CaseRecord() {
  const { id } = useParams();
  const { leads, me, isAdmin, onStatusPick, onNowClear, reload } = useOutletContext();
  const nav = useNavigate();
  const row = (leads || []).find((l) => l.id === id);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("thread");
  const [params, setParams] = useSearchParams();
  const pick = useCallback((k) => onStatusPick(id, k), [onStatusPick, id]);
  const dropAuto = useCallback(() => setParams({}, { replace: true }), [setParams]);
  const cc = useCompanyCheck(id, "lender");
  const hasCheck = !!cc.data && !["none", "skipped"].includes(cc.data.status);
  const openReport = () => { setTab("company"); document.getElementById("lender-case-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const pitch = useLenderPitch(id);
  const [sending, setSending] = useState(null);
  const [sentTick, setSentTick] = useState(0);
  const pitched = pitch.data?.status === "approved";
  const openPitch = () => { setTab("pitch"); document.getElementById("lender-case-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/lender/leads/${id}`)
      .then((r) => { if (alive) setLead(r.data); })
      .catch(() => { if (alive) setLead(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return <Empty>Opening the case…</Empty>;

  if (!lead) {
    return (
      <Panel testId="lender-case-missing">
        <Empty>
          That case isn&apos;t in your queue.
          <div style={{ marginTop: 16 }}><Btn as={Link} to="/lender">Back to your desk</Btn></div>
        </Empty>
      </Panel>
    );
  }

  const sla = me?.turnaround_days || 3;
  const st = deskState(row || lead, sla);
  const due = decisionDue(row || lead, sla);
  const files = lead.files || [];

  const rail = (
    <>
      <Panel title="Your decision clock" testId="lender-case-clock">
        {["completed", "declined"].includes(row?.status) ? (
          <>
            <div className="money" style={{ fontSize: 26 }}>{row?.status === "completed" ? "Funded" : "Closed"}</div>
            <div className="money-sub">{row?.decline_reason || "Fee invoiced against what you earned"}</div>
          </>
        ) : row?.status === "offer_made" ? (
          <>
            <div className="money" style={{ fontSize: 26 }} data-testid="lender-case-clock-decided">Decided</div>
            <div className="money-sub">{st.sub} · your clock has stopped</div>
          </>
        ) : (
          <>
            {(row?.sla || lead.sla)?.state === "waiting_client" ? (
              <>
                <div className="money" style={{ fontSize: 26 }} data-testid="lender-case-clock-paused">Paused</div>
                <div className="money-sub">
                  {row?.step === "valuation" ? "Waiting on the valuer or solicitors · your " : "Waiting on the client · your "}
                  {sla}-day SLA picks up {row?.step === "valuation" ? "when the results are back" : "when they reply"}
                </div>
              </>
            ) : ["chasing", "overdue"].includes((row?.sla || lead.sla)?.state) ? (
              <>
                <div className="money attn" style={{ fontSize: 26 }} data-testid="lender-case-clock-due">Due now</div>
                <div className="money-sub">A decision is due now on this case · your {sla}-day SLA</div>
              </>
            ) : (
              <>
                <div className="money" style={{ fontSize: 26, color: due?.state === "late" ? "var(--attn-tx)" : due?.state === "today" ? "#8A5200" : undefined }}>
                  {due?.label || "—"}
                </div>
                <div className="money-sub">Due {due ? ukDayTime(new Date(due.due).toISOString()) : "—"} · your {sla}-day SLA</div>
              </>
            )}
            <SlaCountdown sla={row?.sla || lead.sla} step={row?.step} variant="lender" testId="lender-case-sla" />
          </>
        )}
        <Divider style={{ margin: "18px 0" }} />
        <Kv rows={[
          ["Arrived", ukDayYear(row?.sent_at || lead.sent_at, "—")],
          ["Facility", gbp(lead.amount_gbp)],
          ["Documents open", String(row?.docs_open || 0)],
        ]} />
      </Panel>

      <DeskSupport body="Anything odd about this one? Ask before you decline it — half of these are a packaging problem, not a credit problem."
                   testId="lender-case-support" />

      <RailCard title="Your contact there" testId="lender-case-contact">
        <Kv rows={[
          ["Name", lead.applicant_name],
          ["Position", lead.applicant_position || "—"],
          ["Email", <span style={{ fontSize: 13 }} key="e">{lead.applicant_email || "—"}</span>],
          ["Phone", lead.applicant_phone || "—"],
        ]} />
        <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "14px 0 0" }}>
          You deal with the client directly from here. The introducer stays out of the credit conversation.
        </p>
      </RailCard>

      {files.length > 0 && !lead.closed && (
        <RailCard title="Documents" testId="lender-case-doclinks">
          {files.slice(0, 6).map((f) => (
            <LinkRow key={f.id} label={f.filename} onClick={() => setTab("documents")} testId={`lender-doclink-${f.id}`} />
          ))}
        </RailCard>
      )}

      <Note>Declining with a reason is what keeps the next case we send you worth opening.</Note>
    </>
  );

  return (
    <>
      <button onClick={() => nav("/lender")} data-testid="lender-case-back"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, background: "none", cursor: "pointer",
                font: '700 12.5px "Instrument Sans"', color: "var(--mut)", padding: 0, marginBottom: 14, whiteSpace: "nowrap" }}>
        <I.back /> Your desk
      </button>

      <PageHead
        eyebrow="Case record"
        title={lead.business_name || lead.applicant_name}
        testId="lender-lead-detail"
        meta={
          <>
            <StatusChip state={st} testId="lender-case-state" />
            {st.key === "awaiting_docs" && <DocsProgress progress={row?.docs_progress} variant="inline" testId="lender-case-docs-progress" />}
            <span style={{ color: "var(--mut)", fontSize: 13 }}>{lead.finance_type} · {gbp(lead.amount_gbp)}{lead.term_months ? ` over ${lead.term_months} months` : ""}</span>
            <span style={{ width: 1, height: 16, background: "var(--line-2)" }} />
            <span style={{ color: "var(--mut)", fontSize: 13 }}>Arrived {ukDayYear(row?.sent_at || lead.sent_at, "—")}</span>
            {lead.introduced_by && (
              <span style={{ color: "var(--ink)", fontSize: 13, fontWeight: 600 }} data-testid="lender-case-introduced-by">
                Introduced by {lead.introduced_by}
              </span>
            )}
          </>
        }
        actions={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <StagePicker value={row?.step || row?.status} state={st} docs={row?.docs_open || 0} disabled={isAdmin || lead.closed}
                         onPick={(v) => onStatusPick(lead.id, v)} testId="lender-detail-status" />
            {row && !lead.closed && (
              <NowPicker now={nowOf(row)} status={row.status} disabled={isAdmin} onPick={(k) => onStatusPick(lead.id, k)}
                         onClear={() => onNowClear(lead.id)} testId="lender-detail-now" />
            )}
          </div>
        }
      />

      <PageBody rail={rail}>
        {!isAdmin && row && (
          <CallCheck row={row} client={lead.applicant_name} onPick={pick} onClear={() => onNowClear(id)}
                     auto={params.get("call")} onAuto={dropAuto} testId="lender-case-callcheck" />
        )}
        {!isAdmin && row?.suggest && (
          <div style={{ marginBottom: 14 }}>
            <StageNudge leadId={lead.id} suggest={row.suggest} onPick={onStatusPick} onDone={reload} testId="lender-case-nudge" />
          </div>
        )}
        <div className="card">
          <div className="card-b">
            <Ladder steps={ladderFor(row?.status || "sent", st)} />
            <LiveLine move={row?.move || lead.move} testId="lender-case-live" />
            <Divider />
            <div className="case-figures" data-testid="lender-case-strip">
              <div><div className="rail-t" style={{ marginBottom: 7 }}>Facility sought</div><div className="money" style={{ fontSize: 22 }}>{gbp(lead.amount_gbp)}</div></div>
              <div><div className="rail-t" style={{ marginBottom: 7 }}>Term</div><div className="money" style={{ fontSize: 22 }}>{lead.term_months ? `${lead.term_months} mo` : "Open"}</div></div>
              <div><div className="rail-t" style={{ marginBottom: 7 }}>Finance type</div><div className="money" style={{ fontSize: 22 }}>{lead.finance_type}</div></div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Urgency</div>
                <div className="money" style={{ fontSize: 22, textTransform: "capitalize" }}>{(lead.urgency || "—").replace(/_/g, " ")}</div>
              </div>
            </div>
          </div>
        </div>

        {pitched && !lead.closed && (
          <div style={{ marginTop: 22 }}>
            <DealPitchCard p={pitch.data} onRead={openPitch} onPack={() => pitch.pack(lead.business_name, lead.reference)}
                           onSend={() => setSending("deal_pack")} />
          </div>
        )}

        {hasCheck && !lead.closed && (
          <div style={{ marginTop: 22 }}>
            <CompanyCheckCard cc={cc} role="lender" onOpen={openReport} onEmail={() => setSending("company_check")} testId="lender-company-check" />
          </div>
        )}

        {!lead.closed && (
          <div style={{ marginTop: 22 }}>
            <SentLog leadId={id} tick={sentTick} broker={pitch.data?.broker || cc.data?.broker} />
          </div>
        )}

        {sending && (
          <SendDialog leadId={id} doc={sending} company={lead.business_name}
                      onClose={(sent) => { setSending(null); if (sent) setSentTick((t) => t + 1); }} testId="lender-send-dialog" />
        )}

        <div style={{ marginTop: 22 }}>
          <UpdateHistory lanes={[{ name: "Your desk", updates: row?.updates || lead.updates }]} aud="desk" testId="lender-case-updates" />
        </div>

        {lead.closed && (lead.withdrawn ? (
          <div className="warn" style={{ marginTop: 22 }} data-testid="lender-lead-withdrawn">
            <span>
              <b>Connexis has taken this case back{lead.withdrawn_reason ? ` — ${lead.withdrawn_reason.toLowerCase()}` : ""}.</b>{" "}
              {lead.withdrawn_note ? <>&ldquo;{lead.withdrawn_note}&rdquo;{" "}</> : null}
              You did not pass on it and nothing goes on your record. We emailed and texted you at the time.
              The client&apos;s documents have come off your portal and the thread is closed on your side —
              if you did have a view on it, message the desk and we will happily put it back with you.
            </span>
          </div>
        ) : (
          <div className="warn" style={{ marginTop: 22 }} data-testid="lender-lead-closed">
            <span>
              <b>You passed on this case{lead.closed_reason ? ` — ${lead.closed_reason}` : ""}.</b>{" "}
              Your decision and your own notes stay on file. The client&apos;s documents and the live thread have closed
              on your side — message the Connexis desk if you&apos;d like another look and we&apos;ll re-open it.
            </span>
          </div>
        ))}

        <div style={{ marginTop: 34, scrollMarginTop: 80 }} id="lender-case-tabs">
          <Tabs
            value={tab}
            onChange={setTab}
            testPrefix="lender-case-tab"
            items={[
              { key: "thread", label: "Thread" },
              { key: "overview", label: "Overview" },
              ...(pitched && !lead.closed ? [{ key: "pitch", label: "Deal pitch" }] : []),
              ...(hasCheck && !lead.closed ? [{ key: "company", label: "Company check" }] : []),
              { key: "documents", label: "Documents", count: files.length },
            ]}
          />
        </div>

        {tab === "company" && hasCheck && <CompanyCheckReport cc={cc} testId="lender-company-report" />}
        {tab === "pitch" && pitched && <DealPitchView p={pitch.data} testId="lender-pitch" />}

        {tab === "thread" && (
          <>
            <Panel
              title="Everything on this case, in order"
              sub="Your notes to the desk, documents as they land and every decision — one feed. Nothing you write here ever reaches the introducer unless we pass it on."
              testId="lender-case-thread"
            >
              <CaseThread leadId={id} testPrefix="lender-thread" />
            </Panel>
            <Sec>
              <AskTheClient leadId={id} disabled={!!lead.closed} testPrefix="lender-ask" />
            </Sec>
          </>
        )}

        {tab === "overview" && (
          <>
            <div className="grid2">
              <Panel title="The business" testId="lender-case-business">
                <Kv rows={[
                  ["Legal name", lead.business_name || "—"],
                  ["Trading name", lead.trading_name || "—"],
                  ["Companies House", lead.company_number || "—"],
                  ["VAT", lead.vat_number || "—"],
                  ["Incorporated", lead.incorporation_date || "—"],
                  ["Sector", lead.industry_sector || "—"],
                  ["Employees", lead.employees != null ? String(lead.employees) : "—"],
                ]} />
                {lead.company_address && (
                  <p style={{ marginTop: 14, borderTop: "1px solid var(--line-soft)", paddingTop: 12, fontSize: 12.5, color: "var(--mut)" }}>
                    {lead.company_address}
                  </p>
                )}
              </Panel>

              <Panel title="Financials" testId="lender-case-financials">
                <Kv rows={[
                  ["Annual revenue", lead.annual_revenue_gbp ? gbp(lead.annual_revenue_gbp) : "—"],
                  ["Monthly revenue", lead.monthly_revenue_gbp ? gbp(lead.monthly_revenue_gbp) : "—"],
                  ["Net profit", lead.net_profit_gbp != null ? gbp(lead.net_profit_gbp) : "—"],
                  ["Existing debt", lead.existing_debt === true ? "Yes" : lead.existing_debt === false ? "No" : "Unknown"],
                  ["PG available", lead.personal_guarantee_available === true ? "Yes" : lead.personal_guarantee_available === false ? "No" : "Unknown"],
                ]} />
              </Panel>
            </div>

            <Sec title="The deal" sub="What the introducer packaged, in their words.">
              <Panel testId="lender-case-deal">
                <Block label="Loan purpose" value={lead.loan_purpose} />
                <Block label="Security offered" value={lead.security_offered} />
                <Block label="Existing debt summary" value={lead.existing_debt_summary} />
                <Block label="What the introducer told us" value={lead.notes} />
                {!lead.loan_purpose && !lead.security_offered && !lead.notes && (
                  <CellSub style={{ margin: 0 }}>Nothing written up beyond the figures above.</CellSub>
                )}
              </Panel>
            </Sec>

            <Sec title="What happens next" sub="Four moves, and the introducer sees each one the moment you make it.">
              <Panel>
                <Tb>
                  {[
                    ["Under assessment", "Tells the introducer somebody is actually reading it. Post a Right now update as it moves."],
                    ["Ask for anything missing", "Don't sit on a case for want of a bank statement."],
                    ["Offer issued", "Your terms land on the introducer's case, side by side with the other desks."],
                    ["Funded", "We invoice our fee against what you earned."],
                  ].map(([t, b], i) => (
                    <tr key={t}>
                      <td className="lead" style={{ width: 54 }}><div className="money" style={{ fontSize: 17 }}>{i + 1}</div></td>
                      <Td><CellMain>{t}</CellMain><CellSub>{b}</CellSub></Td>
                    </tr>
                  ))}
                </Tb>
              </Panel>
            </Sec>
          </>
        )}

        {tab === "documents" && (
          <>
            <Panel title={`Documents (${files.length})`} sub="Everything the applicant and the introducer have put on the case."
                   testId="lender-case-documents">
              {files.length === 0
                ? <CellSub style={{ margin: 0 }}>{lead.closed ? "Closed on your side." : "Nothing attached yet."}</CellSub>
                : <Tb head={<><Th>File</Th><Th>Size</Th><Th className="r">Get it</Th></>}>
                  {files.map((f) => <FileRow key={f.id} file={f} />)}
                </Tb>}
            </Panel>

            <Sec title="Need something to decide?"
                 sub="Ask here. The desk and the introducer are told instantly, and the client gets a private upload link under Connexis.">
              <Panel testId="lender-case-docreqs">
                <DocRequests leadId={id} role="lender" testPrefix="lender-docreq" />
              </Panel>
            </Sec>
          </>
        )}
      </PageBody>
    </>
  );
}

const Block = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="rail-t" style={{ marginBottom: 7 }}>{label}</div>
      <p style={{ margin: 0, whiteSpace: "pre-wrap", borderLeft: "2px solid var(--faded)", paddingLeft: 13,
        fontSize: 13.5, lineHeight: 1.65, color: "var(--ink-3)" }}>{value}</p>
    </div>
  );
};

function FileRow({ file }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/hub/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: r.data.type || file.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`lender-file-${file.id}`}>
      <td className="lead"><CellMain>{file.filename}</CellMain><DateCell>{ukDayYear(file.uploaded_at, "")}</DateCell></td>
      <Td><CellSub style={{ margin: 0 }}>{prettySize(file.size)}</CellSub></Td>
      <Td className="r"><Btn sm onClick={dl} disabled={busy}>{busy ? "…" : "Download"}</Btn></Td>
    </tr>
  );
}
